import Phaser from 'phaser';
import { Capacitor } from '@capacitor/core';
import { GAME_CONFIG } from '../config';
import { COLORS, CSS, FONT } from '../theme';
import { setLetterboxColor } from '../utils/letterbox';
import { t, getLocale, type Locale } from '../i18n';
import { appSettings } from '../services/AppSettings';
import * as Telemetry from '../services/Telemetry';
import { getAuth } from '../services/AuthService';
import { getAdReward } from '../services/AdReward';
import { getLayoutFrame } from '../utils/layout';

const VERSION = '0.1.0-alpha';

export class SettingsScene extends Phaser.Scene {
  constructor() {
    super('SettingsScene');
  }

  create() {
    appSettings.load();
    const { STAGE_W, STAGE_H } = GAME_CONFIG;
    const frame = getLayoutFrame(this);
    setLetterboxColor(COLORS.cream);
    this.add.rectangle(0, 0, STAGE_W, STAGE_H, COLORS.cream).setOrigin(0, 0);

    // ── 헤더
    this.add
      .text(STAGE_W / 2, frame.fromTop(120), t('settings.title'), {
        fontFamily: FONT.display, fontSize: '56px',
        color: CSS.ink, fontStyle: '900',
      }).setOrigin(0.5);

    const backBtn = this.add
      .text(60, frame.fromTop(60), '◀ ' + t('settings.back'), {
        fontFamily: FONT.display, fontSize: '28px',
        color: CSS.ink, fontStyle: '700',
      }).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });
    backBtn.on('pointerup', () => this.scene.start('HomeScene'));

    // ── 본문 항목 — 카드 형식 단일 컬럼 (간단 임시 레이아웃)
    const startY = frame.fromTop(240);
    const rowH = 100;
    let y = startY;

    y = this.drawToggleRow(y, t('settings.bgm'), appSettings.get().bgmEnabled, (v) => {
      appSettings.setBgm(v);
      Telemetry.logEvent('settings_bgm', { enabled: String(v) });
    }); y += rowH;

    y = this.drawToggleRow(y, t('settings.sfx'), appSettings.get().sfxEnabled, (v) => {
      appSettings.setSfx(v);
      Telemetry.logEvent('settings_sfx', { enabled: String(v) });
    }); y += rowH;

    y = this.drawVolumeRow(y, appSettings.get().masterVolume, (v) => {
      appSettings.setVolume(v);
    }); y += rowH;

    y = this.drawLanguageRow(y, appSettings.get().locale); y += rowH;

    // ── 법무 링크 (placeholder — Phase ε 에서 실 URL 연결)
    y = this.drawLinkRow(y, t('settings.privacy'), () =>
      console.log('[settings] privacy link — placeholder')); y += rowH;
    y = this.drawLinkRow(y, t('settings.terms'), () =>
      console.log('[settings] terms link — placeholder')); y += rowH;

    // ── α.6 검증 패널 (개발 빌드 + native 빌드에서 노출) — Phase β 진입 시 제거 예정
    const showDevPanel = import.meta.env.DEV || Capacitor.isNativePlatform();

    // ── 버전 — dev 패널이 노출되면 패널 위로 끌어올림
    this.add
      .text(STAGE_W / 2, frame.fromBottom(showDevPanel ? 380 : 200), `${t('settings.version')} ${VERSION}`, {
        fontFamily: FONT.body, fontSize: '24px', color: CSS.inkSoft,
      }).setOrigin(0.5);

    if (showDevPanel) {
      const status = this.add
        .text(STAGE_W / 2, frame.fromBottom(40), '', {
          fontFamily: FONT.body, fontSize: '18px', color: CSS.inkSoft,
          align: 'center', wordWrap: { width: STAGE_W - 80 },
        }).setOrigin(0.5);

      const setStatus = (msg: string) => status.setText(msg);

      const crashBtn = this.add
        .text(STAGE_W / 2, frame.fromBottom(290), '[α.6] Force Crash', {
          fontFamily: FONT.body, fontSize: '20px', color: '#cc4444',
        }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      crashBtn.on('pointerup', () => {
        Telemetry.forceCrash().catch((err) => console.warn('forceCrash', err));
      });

      const adBtn = this.add
        .text(STAGE_W / 2, frame.fromBottom(240), '[α.6.5] Show Rewarded Ad', {
          fontFamily: FONT.body, fontSize: '20px', color: '#33aa55',
        }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      adBtn.on('pointerup', async () => {
        setStatus('광고 로딩 중...');
        try {
          const provider = await getAdReward();
          const outcome = await provider.showRewarded();
          setStatus(`광고 결과: ${outcome}`);
          console.log('[α.6.5] showRewarded', outcome);
        } catch (err) {
          setStatus(`✗ ${(err as Error).message ?? err}`);
          console.warn('[α.6.5] showRewarded FAIL', err);
        }
      });

      const appleBtn = this.add
        .text(STAGE_W / 2, frame.fromBottom(190), '[α.6.4] Sign in with Apple', {
          fontFamily: FONT.body, fontSize: '20px', color: '#4477cc',
        }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      appleBtn.on('pointerup', async () => {
        setStatus('Apple 시트 호출 중...');
        try {
          const auth = await getAuth();
          const u = await auth.signInWithApple();
          setStatus(`✓ ${u.provider} uid:${u.uid.slice(0, 10)}…`);
          console.log('[α.6.4] signInWithApple OK', u);
        } catch (err) {
          setStatus(`✗ ${(err as Error).message ?? err}`);
          console.warn('[α.6.4] signInWithApple FAIL', err);
        }
      });

      const googleBtn = this.add
        .text(STAGE_W / 2, frame.fromBottom(140), '[α.6.4] Sign in with Google', {
          fontFamily: FONT.body, fontSize: '20px', color: '#4477cc',
        }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      googleBtn.on('pointerup', async () => {
        setStatus('Google 시트 호출 중...');
        try {
          const auth = await getAuth();
          const u = await auth.signInWithGoogle();
          setStatus(`✓ ${u.provider} uid:${u.uid.slice(0, 10)}…`);
          console.log('[α.6.4] signInWithGoogle OK', u);
        } catch (err) {
          setStatus(`✗ ${(err as Error).message ?? err}`);
          console.warn('[α.6.4] signInWithGoogle FAIL', err);
        }
      });

      const signOutBtn = this.add
        .text(STAGE_W / 2, frame.fromBottom(90), '[α.6.4] Sign out', {
          fontFamily: FONT.body, fontSize: '18px', color: CSS.inkSoft,
        }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      signOutBtn.on('pointerup', async () => {
        try {
          const auth = await getAuth();
          await auth.signOut();
          setStatus('로그아웃 완료 (재부팅 시 익명 재발급)');
        } catch (err) {
          setStatus(`✗ signOut: ${(err as Error).message ?? err}`);
        }
      });
    }

    this.game.events.on('safeAreaChanged', this.handleSafeAreaChanged, this);
    this.events.once('shutdown', () => {
      this.game.events.off('safeAreaChanged', this.handleSafeAreaChanged, this);
    });
  }

  private handleSafeAreaChanged = () => {
    if (!this.scene.isActive()) return;
    this.scene.restart();
  };

  private drawToggleRow(
    y: number,
    label: string,
    value: boolean,
    onChange: (v: boolean) => void,
  ): number {
    const { STAGE_W } = GAME_CONFIG;
    this.add.text(80, y, label, {
      fontFamily: FONT.body, fontSize: '32px',
      color: CSS.ink, fontStyle: '700',
    }).setOrigin(0, 0.5);
    const toggle = this.add
      .text(STAGE_W - 80, y, value ? t('common.on') : t('common.off'), {
        fontFamily: FONT.display, fontSize: '32px',
        color: value ? CSS.coral : CSS.inkFaint, fontStyle: '900',
      })
      .setOrigin(1, 0.5)
      .setInteractive({ useHandCursor: true });
    toggle.on('pointerup', () => {
      const next = !value;
      onChange(next);
      toggle.setText(next ? t('common.on') : t('common.off'));
      toggle.setColor(next ? CSS.coral : CSS.inkFaint);
      value = next;
    });
    return y;
  }

  private drawVolumeRow(
    y: number,
    initial: number,
    onChange: (v: number) => void,
  ): number {
    const { STAGE_W } = GAME_CONFIG;
    this.add.text(80, y, t('settings.volume'), {
      fontFamily: FONT.body, fontSize: '32px',
      color: CSS.ink, fontStyle: '700',
    }).setOrigin(0, 0.5);
    const valueText = this.add
      .text(STAGE_W - 80, y, `${initial}`, {
        fontFamily: FONT.display, fontSize: '32px',
        color: CSS.ink, fontStyle: '900',
      })
      .setOrigin(1, 0.5);
    // 임시 −/+ 버튼 — Phase β에서 디자인된 슬라이더로 교체.
    const minus = this.add
      .text(STAGE_W - 220, y, '−', {
        fontFamily: FONT.display, fontSize: '40px',
        color: CSS.ink, fontStyle: '900',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    const plus = this.add
      .text(STAGE_W - 160, y, '+', {
        fontFamily: FONT.display, fontSize: '40px',
        color: CSS.ink, fontStyle: '900',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    let cur = initial;
    minus.on('pointerup', () => {
      cur = Math.max(0, cur - 10);
      onChange(cur);
      valueText.setText(`${cur}`);
    });
    plus.on('pointerup', () => {
      cur = Math.min(100, cur + 10);
      onChange(cur);
      valueText.setText(`${cur}`);
    });
    return y;
  }

  private drawLanguageRow(y: number, initial: Locale): number {
    const { STAGE_W } = GAME_CONFIG;
    this.add.text(80, y, t('settings.language'), {
      fontFamily: FONT.body, fontSize: '32px',
      color: CSS.ink, fontStyle: '700',
    }).setOrigin(0, 0.5);
    const langBtn = this.add
      .text(STAGE_W - 80, y, t(`settings.language.${initial}`), {
        fontFamily: FONT.display, fontSize: '28px',
        color: CSS.coral, fontStyle: '900',
      })
      .setOrigin(1, 0.5)
      .setInteractive({ useHandCursor: true });
    langBtn.on('pointerup', () => {
      const next: Locale = getLocale() === 'en' ? 'ko' : 'en';
      appSettings.setLocale(next);
      Telemetry.logEvent('settings_locale', { locale: next });
      // 단순 재시작 — 모든 t() 호출이 새 로케일로 재평가됨.
      this.scene.restart();
    });
    return y;
  }

  private drawLinkRow(y: number, label: string, onClick: () => void): number {
    const row = this.add
      .text(80, y, label + ' ›', {
        fontFamily: FONT.body, fontSize: '28px',
        color: CSS.ink, fontStyle: '700',
      })
      .setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true });
    row.on('pointerup', onClick);
    return y;
  }
}
